import type {
  RegionalHighlightPurchaseDurationMonths,
  RegionalHighlightPurchaseOption,
  RegionalHighlightPurchaseOptionsResponse,
} from '@/types/regionalHighlight';

type RecordValue = Record<string, unknown>;

const DURATION_MONTHS: RegionalHighlightPurchaseDurationMonths[] = [1, 3, 6, 12];

const asRecord = (value: unknown): RecordValue | undefined =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as RecordValue)
    : undefined;

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const asFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const asNonNegativeSafeInteger = (value: unknown): number | undefined => {
  const parsed = asFiniteNumber(value);
  if (parsed === undefined || !Number.isSafeInteger(parsed) || parsed < 0) {
    return undefined;
  }
  return parsed;
};

const asBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (value === 'true') {
    return true;
  }
  if (value === 'false') {
    return false;
  }
  return undefined;
};

const normalizeDistrictNames = (value: unknown): Record<string, string> | undefined => {
  const names = asRecord(value);
  if (!names) return undefined;

  return Object.entries(names).reduce<Record<string, string>>((result, [locale, label]) => {
    if (typeof label === 'string') result[locale] = label;
    return result;
  }, {});
};

const durationKey = (value: string): RegionalHighlightPurchaseDurationMonths | undefined => {
  const months = Number(value);
  return DURATION_MONTHS.includes(months as RegionalHighlightPurchaseDurationMonths)
    ? (months as RegionalHighlightPurchaseDurationMonths)
    : undefined;
};

const normalizeDurationNumberMap = (
  value: unknown,
): Partial<Record<RegionalHighlightPurchaseDurationMonths, number>> => {
  const raw = asRecord(value);
  if (!raw) return {};

  return Object.entries(raw).reduce<
    Partial<Record<RegionalHighlightPurchaseDurationMonths, number>>
  >((result, [key, entry]) => {
    const months = durationKey(key);
    const amount = asNonNegativeSafeInteger(entry);
    if (months !== undefined && amount !== undefined) result[months] = amount;
    return result;
  }, {});
};

const normalizeDurationStringMap = (
  value: unknown,
): Partial<Record<RegionalHighlightPurchaseDurationMonths, string>> => {
  const raw = asRecord(value);
  if (!raw) return {};

  return Object.entries(raw).reduce<
    Partial<Record<RegionalHighlightPurchaseDurationMonths, string>>
  >((result, [key, entry]) => {
    const months = durationKey(key);
    const code = asString(entry);
    if (months !== undefined && code) result[months] = code;
    return result;
  }, {});
};

const normalizeOption = (value: unknown): RegionalHighlightPurchaseOption | undefined => {
  const raw = asRecord(value);
  const districtId = asString(raw?.districtId) ?? asString(raw?.district);
  const districtLabel = asString(raw?.districtLabel) ?? districtId;
  const positionsTotal = asNonNegativeSafeInteger(raw?.positionsTotal);
  const positionsSold = asNonNegativeSafeInteger(raw?.positionsSold);
  const positionsFree = asNonNegativeSafeInteger(raw?.positionsFree);
  const searchesPerMonth = asNonNegativeSafeInteger(raw?.searchesPerMonth) ?? 0;
  const purchasable = asBoolean(raw?.purchasable) ?? asBoolean(raw?.isPurchasable);

  if (
    !raw ||
    !districtId ||
    !districtLabel ||
    positionsTotal === undefined ||
    positionsSold === undefined ||
    positionsFree === undefined ||
    purchasable === undefined
  ) {
    return undefined;
  }

  const positionsHeldByCustomer = asNonNegativeSafeInteger(raw?.positionsHeldByCustomer);

  return {
    districtId,
    districtLabel,
    districtName: normalizeDistrictNames(raw.districtName),
    positionsTotal,
    positionsSold,
    positionsFree,
    monthlyPriceByDuration: normalizeDurationNumberMap(
      raw.monthlyPriceByDuration ?? raw.monthlyPriceChfCentsByDuration,
    ),
    productCodeByDuration: normalizeDurationStringMap(raw.productCodeByDuration),
    searchesPerMonth,
    purchasable,
    purchasableReason: asString(raw.purchasableReason ?? raw.notPurchasableReason ?? raw.reason),
    alreadyHoldsPosition:
      typeof raw.alreadyHoldsPosition === 'boolean'
        ? raw.alreadyHoldsPosition
        : positionsHeldByCustomer !== undefined
          ? positionsHeldByCustomer > 0
          : undefined,
  };
};

const optionsFrom = (value: unknown): unknown[] => {
  if (Array.isArray(value)) return value;
  const raw = asRecord(value);
  if (!raw) return [];
  for (const key of ['options', 'districts', 'purchaseOptions'] as const) {
    const entries = raw[key];
    if (Array.isArray(entries)) {
      return entries;
    }
  }
  return [];
};

export const normalizeRegionalHighlightPurchaseOptionsResponse = (
  value: unknown,
): RegionalHighlightPurchaseOptionsResponse => ({
  options: optionsFrom(value)
    .map(normalizeOption)
    .filter((option): option is RegionalHighlightPurchaseOption => Boolean(option)),
});
